import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Gallery } from "@/components/Gallery";
import { Seo } from "@/components/Seo";
import { PRODUCTION_GALLERY, PRODUCTS_GALLERY, CATALOG_PDFS } from "@/data/media";
import { Check, ShieldCheck, ArrowRight, Download } from "lucide-react";

const LANGS = [
  { code: "it", label: "Italiano" },
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
];

export default function Production() {
  const { t, lang } = useLang();
  const downloadCatalog = (l) => {
    const code = l || lang;
    window.open(CATALOG_PDFS[code] || CATALOG_PDFS.it, "_blank");
  };

  return (
    <div data-testid="production-page">
      <Seo page="production" />
      <PageHero overline={t.production.overline} title={t.production.title} image={PRODUCTION_GALLERY[0]} intro={t.production.intro} />

      <section className="py-16 md:py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Gallery images={PRODUCTION_GALLERY} testid="production-gallery" />
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-sand">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7 space-y-6">
            <Reveal><p className="text-ink/75 text-lg leading-relaxed">{t.production.p1}</p></Reveal>
            <Reveal delay={100}><p className="text-ink/75 text-lg leading-relaxed">{t.production.p2}</p></Reveal>
            <Reveal delay={150}>
              <p data-testid="production-custom-line" className="text-ink text-lg leading-relaxed border-l-2 border-brass pl-5 font-medium">
                {t.production.customLine}
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={150}>
              <div className="bg-white p-8 md:p-10 border-t-2 border-brass shadow-sm">
                <h3 className="font-serif-display text-2xl text-ink mb-6">{t.production.techTitle}</h3>
                <ul className="space-y-4">
                  {t.production.tech.map((item, i) => (
                    <li key={i} className="flex gap-3 text-ink/80">
                      <Check className="w-5 h-5 text-brass shrink-0 mt-0.5" />
                      <span className="text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Realizzazioni */}
      <section className="py-16 md:py-24 bg-cream" data-testid="production-works">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-12">
            <p className="overline mb-4">{t.products.overline}</p>
            <h2 className="font-serif-display font-light text-4xl md:text-5xl tracking-tight text-ink">
              {t.products.title}
            </h2>
            <p className="mt-5 text-ink/65 leading-relaxed">{t.products.intro}</p>
          </Reveal>
          <Reveal>
            <Gallery images={PRODUCTS_GALLERY} testid="products-gallery" />
          </Reveal>
        </div>
      </section>

      {/* Catalogo */}
      <section className="py-16 md:py-24 bg-ink" data-testid="products-catalog-banner">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="border border-brass/30 px-8 py-12 md:px-16 md:py-16 text-center relative overflow-hidden">
              <span className="overline text-brass">{t.products.catalogBanner.overline}</span>
              <h2 className="mt-4 font-serif-display font-light text-3xl md:text-5xl tracking-tight text-white">
                {t.products.catalogBanner.title}
              </h2>
              <p className="mt-6 max-w-2xl mx-auto text-white/60 leading-relaxed text-sm md:text-base">
                {t.products.catalogBanner.text}
              </p>
              <button
                onClick={() => downloadCatalog()}
                data-testid="products-catalog-download-btn"
                className="mt-9 inline-flex items-center gap-2 bg-brass text-white px-9 py-4 text-sm tracking-wide uppercase font-medium hover:bg-brass-dark transition-colors"
              >
                <Download className="w-4 h-4" /> {t.products.catalogBanner.button}
              </button>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                {LANGS.map((lg) => (
                  <button
                    key={lg.code}
                    onClick={() => downloadCatalog(lg.code)}
                    data-testid={`catalog-download-${lg.code}`}
                    className={`inline-flex items-center gap-1.5 border px-4 py-2 text-xs uppercase tracking-wide transition-colors ${lg.code === lang ? "border-brass text-brass" : "border-white/25 text-white/70 hover:border-brass hover:text-brass"}`}
                  >
                    <Download className="w-3 h-3" /> {lg.label}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 md:py-28 bg-sand">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <ShieldCheck className="w-12 h-12 text-brass mx-auto mb-6" strokeWidth={1.2} />
            <p className="overline mb-4">{t.common.madeInItaly}</p>
            <h2 className="font-serif-display font-light text-4xl md:text-5xl tracking-tight text-ink">{t.production.certTitle}</h2>
            <p className="mt-6 text-ink/70 text-lg leading-relaxed max-w-2xl mx-auto">{t.production.certText}</p>
            <Link to="/contatti" data-testid="production-cta-link" className="mt-9 inline-flex items-center gap-2 bg-ink text-white px-9 py-4 text-sm tracking-wide uppercase font-medium hover:bg-brass transition-colors">
              {t.common.requestQuote} <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
